import { zodResolver } from "@hookform/resolvers/zod";
import { Transaction } from "@mysten/sui/transactions";
import { logger } from "@swarm/shared/logger";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { PurchaseLicenseFormSchema } from "@/components/Dashboard/Dashboard.types";
import { config } from "@/config";
import { useSignAndExecuteTransaction } from "@/hooks/dappkit";
import { useRoutes } from "@/hooks/useRoutes/useRoutes";
import currency from "@/lib/currency";
import { useToast } from "@/ui/shadcn/use-toast";

import { useReferralsContext } from "../referrals/useReferralsContext";
import { SuiQueryContext } from "./SuiQueryContext";
import {
  CurrentAccountCoins,
  KOLWalletsReferralCodeCheckObj,
  LicensePriceObjectFields,
  MergeCoinObjectsTxArgs,
  OnTransactionResultCallbacks,
  OwnedLicenses,
  OwnedReferrer,
  OwnedTransactions,
  PurchaseLicenseMoveCallArgs,
  Referrer,
  SuiQueryContextControllerProps,
  SuiQueryContextType,
  TransactionResult,
  TreasuryObjectFields,
} from "./SuiQueryContext.types";

export const SuiQueryContextController = ({ children }: SuiQueryContextControllerProps) => {
  const [KOLWalletsReferralCodeCheckObj, setKOLWalletsReferralCodeCheckObj] =
    useState<KOLWalletsReferralCodeCheckObj>({
      KOLWallets: [],
      shouldSetGENESISCodeToConnectedAccount: false,
    });

  const [isReferralCodeSet, setIsReferralCodeSet] = useState(false);

  const [isReferralCodeFieldChecked, setIsReferralCodeFieldChecked] = useState(false);

  const [transactionOutput, setTransaction] = useState<TransactionResult>({
    purchaseLicense: {
      onSuccess: undefined,
    },
    mergeCoinObjects: {
      onSuccess: undefined,
    },
  });

  const [licensePrice, setLicensePrice] = useState<LicensePriceObjectFields>({
    current: {
      raw: 0n,
      text: "999.00",
      formatted: `999.00 ${currency.constants.DEFAULT_CURRENCY_TICKER}`,
    },
    next: {
      raw: 0n,
      text: "999.00",
      formatted: `999.00 ${currency.constants.DEFAULT_CURRENCY_TICKER}`,
    },
  });

  const [treasury, setTreasury] = useState<TreasuryObjectFields>({
    sales: "0",
    totalDistribution: "0",
    currentTier: "Tier 1",
    salesToNextTier: "0",
    isPurchasingPaused: false,
    price: {
      current: {
        raw: 999n,
        text: "999.00",
        formatted: `999.00 ${currency.constants.DEFAULT_CURRENCY_TICKER}`,
      },
      next: {
        raw: 999n,
        text: "999.00",
        formatted: `999.00 ${currency.constants.DEFAULT_CURRENCY_TICKER}`,
      },
    },
  });

  const [ownedLicenses, setOwnedLicenses] = useState<OwnedLicenses>({
    count: 0,
    airdropsCount: 0,
    salesCount: 0,
  });

  const [ownedReferrer, setOwnedReferrer] = useState<OwnedReferrer>({
    sales: "0",
    referralsCount: "0",
    rebate: "",
    rebateLevel: {
      value: 0,
      text: "0/50",
      maxReferralsPerTier: 0,
    },
  });

  const [referrer, setReferrer] = useState<Referrer>();

  const [ownedTransactions, setOwnedTransactions] = useState<OwnedTransactions>([]);

  const [currentAccountCoins, setCurrentAccountCoins] = useState<CurrentAccountCoins>({
    coins: [],
    totalCoinBalance: {
      raw: 0n,
      text: "0.00",
      formatted: `0.00 ${currency.constants.DEFAULT_CURRENCY_TICKER}`,
    },
  });

  const [truthCoinBalance, setTruthCoinBalance] = useState<
    | {
        raw: bigint;
        text: string;
        formatted: string;
      }
    | undefined
  >(undefined);

  const [selectedDisplayCoinTicker, setSelectedDisplayCoinTicker] = useState<"USDC" | "TRUTH">(
    "USDC",
  );

  const { officialReferralCode } = useReferralsContext();

  const purchaseLicenseForm = useForm<z.infer<typeof PurchaseLicenseFormSchema>>({
    resolver: zodResolver(PurchaseLicenseFormSchema),
    defaultValues: {
      quantity: "1",
      price: "999.00",
    },
    mode: "all",
  });

  // The terminal-login referral form used to live here too. It moved into
  // `TerminalLogin` because a `useForm` hosted by this provider re-rendered the
  // provider — and every `useSuiQueryContext` consumer — on each keystroke.

  // dapp-kit v2: the wallet signs AND executes, so there is no client-side
  // submit step to override any more. The wrapper rethrows on-chain failures so
  // the `onError` callbacks below still fire (v2 resolves them by default).
  const { mutate: signAndExecuteTransaction } = useSignAndExecuteTransaction();

  const { toast } = useToast();

  const routes = useRoutes();

  function refetchKnownQueries() {
    try {
      ownedLicenses.refetch?.();
      currentAccountCoins.refetch?.();
      treasury.refetch?.();
      licensePrice.refetch?.();
      ownedReferrer.refetch?.();
    } catch (error) {
      logger.error(
        { err: error instanceof Error ? { message: error.message } : error },
        "refetching chain queries failed",
      );
    }
  }

  async function purchaseLicense(
    { quantity, coinObjectId, referralCode }: PurchaseLicenseMoveCallArgs,
    callbacks?: OnTransactionResultCallbacks,
  ) {
    try {
      const tx = new Transaction();

      tx.moveCall({
        target: `${config.packageId}::swarm_license_sale::purchase_license`,
        arguments: [
          tx.object(config.treasuryObjectId!),
          tx.object(coinObjectId),
          tx.pure.u64(quantity),
          tx.pure.string(referralCode),
        ],
      });

      signAndExecuteTransaction(
        {
          transaction: tx,
        },
        {
          onSuccess(data, _variables, _context) {
            setTransaction((prev) => ({
              purchaseLicense: { onSuccess: data },
              mergeCoinObjects: {
                ...prev.mergeCoinObjects,
              },
            }));

            toast({
              title: "Transaction Complete!",
              description: (
                <p className="mb-0">
                  You just purchased {quantity} licenses from {referralCode}.{" "}
                  <a
                    href={routes.sui.explorerUrl({
                      path: `/txblock/${data?.digest}`,
                    })}
                    target="_blank"
                  >
                    {data?.digest}
                  </a>
                </p>
              ),
            });

            callbacks?.onSuccessCallback?.();
          },
          onError(error) {
            logger.error({ err: { message: error.message } }, "purchase license failed");
          },
          onSettled(_data, error) {
            logger.debug({ settledWithError: Boolean(error) }, "purchase license settled");
            callbacks?.onSettledCallback?.();
          },
        },
      );
    } catch (error) {
      logger.error(
        { err: error instanceof Error ? { message: error.message } : error },
        "building purchase license transaction failed",
      );
    }
  }

  // The args object is empty today (the merge always consumes every coin the
  // account owns); it is kept in the signature so adding a selection later does
  // not ripple through every call site.
  async function mergeCoinObjects(
    _args: MergeCoinObjectsTxArgs,
    callbacks?: OnTransactionResultCallbacks,
  ) {
    try {
      const tx = new Transaction();

      const coinObjectsIds = currentAccountCoins.coins.map(
        // MIGRATION: core-API coin objects use `objectId` (was `coinObjectId`).
        (coin) => coin.objectId,
      );
      const coin1 = coinObjectsIds.shift()!;
      const rest = coinObjectsIds.map((coin) => tx.object(coin));

      tx.mergeCoins(tx.object(coin1), rest);

      signAndExecuteTransaction(
        {
          transaction: tx,
        },
        {
          onSuccess(data) {
            setTransaction((prev) => ({
              mergeCoinObjects: {
                onSuccess: data,
              },
              purchaseLicense: { ...prev.purchaseLicense },
            }));

            toast({
              title: "Transaction Complete!",
              description: (
                <p className="mb-0">
                  You just merged your coins.{" "}
                  <a
                    href={routes.sui.explorerUrl({
                      path: `/txblock/${data?.digest}`,
                    })}
                    target="_blank"
                  >
                    {data?.digest}
                  </a>
                </p>
              ),
            });

            callbacks?.onSuccessCallback?.();
          },
          onError(error) {
            logger.error({ err: { message: error.message } }, "merge coin objects failed");
          },
          onSettled(_data, error) {
            logger.debug({ settledWithError: Boolean(error) }, "merge coin objects settled");
            callbacks?.onSettledCallback?.();
          },
        },
      );
    } catch (error) {
      logger.error(
        { err: error instanceof Error ? { message: error.message } : error },
        "building merge coins transaction failed",
      );
    }
  }

  useEffect(() => {
    const subscription = purchaseLicenseForm.watch((value, { name }) => {
      if (name === "quantity") {
        const rawPrice = treasury.price.current.raw * BigInt(value.quantity || "1");
        const decimalPrecisionPrice = currency.convert.toDecimalsPrecisionString(
          rawPrice.toString(),
          6,
        );
        const formatted = currency.convert.toFormattedString(decimalPrecisionPrice);

        purchaseLicenseForm.setValue("price", formatted);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [purchaseLicenseForm.watch, treasury.price.current]);

  useEffect(() => {
    const lsReferralCode = localStorage.getItem("referralCode");

    if (lsReferralCode) {
      purchaseLicenseForm.setValue("referralCode", lsReferralCode);
      setIsReferralCodeSet(true);
      refetchKnownQueries();
    }
  }, [officialReferralCode]);

  useEffect(() => {
    purchaseLicenseForm.setValue(
      "price",
      currency.convert.toDecimalsPrecisionString(treasury.price.current.text, 6),
    );
  }, [treasury.price]);

  useEffect(() => {
    if (!KOLWalletsReferralCodeCheckObj.shouldSetGENESISCodeToConnectedAccount) return;

    purchaseLicenseForm.setValue("referralCode", "GENESIS");
  }, [KOLWalletsReferralCodeCheckObj.shouldSetGENESISCodeToConnectedAccount]);

  const props: SuiQueryContextType = {
    isReferralCodeFieldChecked,
    setIsReferralCodeFieldChecked,
    isReferralCodeSet,
    setIsReferralCodeSet,
    licensePrice,
    setLicensePrice,
    treasury,
    setTreasury,
    ownedLicenses,
    setOwnedLicenses,
    ownedReferrer,
    setOwnedReferrer,
    referrer,
    setReferrer,
    ownedTransactions,
    setOwnedTransactions,
    purchaseLicense,
    transactionOutput,
    currentAccountCoins,
    setCurrentAccountCoins,
    truthCoinBalance,
    setTruthCoinBalance,
    selectedDisplayCoinTicker,
    setSelectedDisplayCoinTicker,
    refetchKnownQueries,
    purchaseLicenseForm,
    KOLWalletsReferralCodeCheckObj,
    setKOLWalletsReferralCodeCheckObj,
    mergeCoinObjects,
  };

  return <SuiQueryContext.Provider value={props}>{children}</SuiQueryContext.Provider>;
};
